import { Grid } from '@mui/joy';
import { Buildings, CurrencyDollar } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';

import { SubtitleCard } from 'core/components/SubtitleCards/SubtitleCard';
import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { InventoryHelper } from 'core/helpers';
import { ICorporatePart } from 'corporate/types';
import { pricingTypeLabelKey } from 'pricing/constants';
import { PricingHelper } from 'pricing/helpers';

interface IProps {
  corporatePart: Partial<ICorporatePart>;
  inline?: boolean;
}

const cardContentSx = { width: 'fit-content', minWidth: '150px' };
const subtitleCardOptions = { contentSx: cardContentSx, cardSx: { height: '100%' } };

export const LegacyPartCards = ({ corporatePart, inline }: IProps) => {
  const { t } = useTranslation();

  const gridSx = {
    xs: 12,
    sm: 6,
    md: inline ? 6 : 4,
    lg: inline ? 6 : 4,
    xl: 4,
  };

  const pricingLabelKey = corporatePart.pricingType
    ? pricingTypeLabelKey[corporatePart.pricingType] ?? null
    : null;

  const pricingTypeLabel = corporatePart.isPnb
    ? t('labelCalculatedFamily')
    : pricingLabelKey
    ? t(pricingLabelKey)
    : null;

  return (
    <Grid container spacing={2} sx={{ margin: '0 -8px !important' }}>
      <Grid {...gridSx}>
        <SubtitleCard
          label={t('labelStandardCostMoq')}
          icon={<CurrencyDollar weight="fill" />}
          value={PricingHelper.formatPricingPerUom(corporatePart.cost, corporatePart.pricingUom)}
          extra={
            corporatePart.purchaseCost
              ? PricingHelper.formatPricingPerUom(
                  corporatePart.purchaseCost,
                  corporatePart.pricingUom
                )
              : corporatePart.cost
              ? DEFAULT_PLACEHOLDER
              : null
          }
          extraTooltip={corporatePart.costingQuantityDiscounts ? t('labelQuantityDiscounts') : null}
          options={subtitleCardOptions}
        />
      </Grid>

      <Grid {...gridSx}>
        <SubtitleCard
          label={t('labelListPrice')}
          icon={<CurrencyDollar weight="bold" />}
          value={PricingHelper.formatPricingPerUom(
            corporatePart.originalListPrice,
            corporatePart.originalListPriceUom
          )}
          extra={pricingTypeLabel}
          options={subtitleCardOptions}
        />
      </Grid>

      <Grid {...gridSx}>
        <SubtitleCard
          label={t('labelInventory')}
          icon={<Buildings weight="fill" />}
          value={InventoryHelper.getInventoryAvailabilityLabel(
            corporatePart.inventory?.totalQty,
            corporatePart.inventory?.uom
          )}
          extra={
            InventoryHelper.isKnownQuantity(corporatePart.inventory?.userLocationQty)
              ? InventoryHelper.getInventoryAvailabilityLabel(
                  corporatePart.inventory.userLocationQty,
                  corporatePart.inventory?.uom
                )
              : corporatePart.inventory?.totalQty
              ? DEFAULT_PLACEHOLDER
              : null
          }
          options={subtitleCardOptions}
        />
      </Grid>
    </Grid>
  );
};
